import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TableColumn, TableAction, TableRow } from '../../../shared/components/data-table/data-table.component';
import { ChannelsService } from '../channels.service';
import { ToolbarFilter } from '../../../shared/components/page-toolbar/page-toolbar.component';
import { HeaderAction } from '../../../shared/components/page-header/page-header.component';
import { ConfirmActionModalData } from '../../../shared/components/confirm-action-modal/confirm-action-modal.model';
import { AuthStateService } from '../../../core/authentication/auth-state.service';

@Component({
  selector: 'app-channel-list',
  standalone: false,
  templateUrl: './channel-list.component.html',
  styleUrls: ['./channel-list.component.scss'],
})
export class ChannelListComponent implements OnInit {
  private channelsService = inject(ChannelsService);
  private router = inject(Router);
  private authState = inject(AuthStateService);

  currentPage = 1;
  itemsPerPage = 12;
  totalResults = 0;
  isLoading = false;

  searchQuery = '';
  selectedStatus = '';

  get headerActions(): HeaderAction[] {
    const actions: HeaderAction[] = [];
    if (this.authState.hasPermission('reports.view')) {
      actions.push({ label: 'Audit log', variant: 'secondary' });
    }
    if (this.authState.hasPermission('channels.create')) {
      actions.push({ label: '+ Create channel', variant: 'primary' });
    }
    return actions;
  }

  filters: ToolbarFilter[] = [
    { key: 'status', label: 'Status', options: ['Active', 'Inactive'] },
  ];

  channelColumns: TableColumn[] = [
    { key: 'name', label: 'Name', type: 'name' },
    { key: 'code', label: 'Code', type: 'text' },
    { key: 'status', label: 'Status', type: 'status' },
    { key: 'activePos', label: 'Active POS', type: 'number' },
    { key: 'historicalPos', label: 'Historical POS', type: 'number' },
    { key: 'lastModified', label: 'Last modified', type: 'date' },
    { key: 'actions', label: '', type: 'actions' },
  ];

  channelRows: TableRow[] = [];

  isModalOpen = false;
  modalData: ConfirmActionModalData | null = null;
  private _pendingActionRow: TableRow | null = null;

  channelActions: TableAction[] = [
    {
      label: 'Activate channel',
      icon: 'assets/icons/password.svg',
      action: (row) => this.openActivateModal(row),
      visible: (row) => row['status'] === 'Inactive' && this.authState.hasPermission('channels.update'),
    },
    {
      label: 'Deactivate channel',
      icon: 'assets/icons/password.svg',
      action: (row) => this.openDeactivateModal(row),
      visible: (row) => row['status'] === 'Active' && this.authState.hasPermission('channels.update'),
    },
    {
      label: 'Delete channel',
      icon: 'assets/icons/password.svg',
      action: (row) => this.openDeleteModal(row),
      visible: () => this.authState.hasPermission('channels.delete'),
    },
  ];

  ngOnInit(): void {
    this.loadChannels();
  }

  loadChannels(): void {
    this.isLoading = true;

    // Map status to lower case format backend expects ("active" or "inactive")
    let statusFilter: any = undefined;
    if (this.selectedStatus === 'Active') {
      statusFilter = 'active';
    } else if (this.selectedStatus === 'Inactive') {
      statusFilter = 'inactive';
    }

    this.channelsService
      .findAll({
        page: this.currentPage,
        limit: this.itemsPerPage,
        search: this.searchQuery || undefined,
        status: statusFilter,
      })
      .subscribe({
        next: (response) => {
          this.channelRows = response.data.map((channel: any) => ({
            id: channel.id,
            name: channel.name,
            code: channel.code,
            avatarType: 'icon',
            avatarIconPath: '/assets/icons/channel-icon.svg',
            status: channel.active ? 'Active' : 'Inactive',
            activePos: channel.activePosCount ?? 0,
            historicalPos: channel.historicalPosCount ?? 0,
            lastModified: channel.updated_at
              ? new Date(channel.updated_at).toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
              : 'Never',
          }));
          this.totalResults = response.total;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load channels:', err);
          this.isLoading = false;
        },
      });
  }

  onHeaderAction(action: HeaderAction): void {
    if (action.label === '+ Create channel') {
      this.router.navigate(['/channels/create']);
    }
  }

  onSearchChange(value: string): void {
    this.searchQuery = value;
    this.currentPage = 1;
    this.loadChannels();
  }

  onFilterChange(filter: ToolbarFilter): void {
    if (filter.key === 'status') {
      this.selectedStatus = filter.value || '';
    }
    this.currentPage = 1;
    this.loadChannels();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadChannels();
  }

  onItemsPerPageChange(items: number): void {
    this.itemsPerPage = items;
    this.currentPage = 1;
    this.loadChannels();
  }

  openActivateModal(row: TableRow): void {
    this._pendingActionRow = row;
    this.modalData = {
      icon: 'unlock',
      title: `Activate **"${row['name']}"**?`,
      titleHighlight: 'success',
      description: `This channel will appear in new POS creation, channel dropdowns, and operational setup flows. **Any previously associated POS will be restored.** You can deactivate the channel at any time.`,
      effects: [
        { type: 'positive', text: `Visible in **new POS creation**, dropdowns, and setup flows` },
        { type: 'positive', text: `${row['activePos']} existing POS keep their channel association` },
        { type: 'positive', text: `${row['historicalPos']} historical POS records and all reports stay accessible` },
      ],
      auditLog: true,
      actions: [{ label: 'Activate channel', style: 'primary', action: 'activate' }],
    };
    this.isModalOpen = true;
  }

  openDeactivateModal(row: TableRow): void {
    this._pendingActionRow = row;
    this.modalData = {
      icon: 'no-entry',
      title: `Deactivate **"${row['name']}"**?`,
      titleHighlight: 'danger',
      description: `This channel will no longer appear in new POS creation, channel dropdowns, or operational setup flows. **Existing POS and historical data remain unchanged.** You can reactivate the channel at any time.`,
      effects: [
        { type: 'negative', text: `Hidden from **new POS creation**, dropdowns, and setup flows` },
        { type: 'positive', text: `${row['activePos']} existing POS keep their channel association` },
        { type: 'positive', text: `${row['historicalPos']} historical POS records and all reports stay accessible` },
      ],
      auditLog: true,
      actions: [{ label: 'Deactivate channel', style: 'danger', action: 'deactivate' }],
    };
    this.isModalOpen = true;
  }

  openDeleteModal(row: TableRow): void {
    this._pendingActionRow = row;
    const hasLinkedPos = (row['activePos'] as number) > 0;

    if (hasLinkedPos) {
      this.modalData = {
        icon: 'block',
        title: `Cannot delete "${row['name']}"`,
        description: `This channel has **${row['activePos']} active POS** linked to it. Deactivate or reassign all active POS before this channel can be deleted.`,
        effects: [
          { type: 'negative', text: `**${row['activePos']} active POS** are blocking deletion` },
          { type: 'positive', text: `${row['historicalPos']} historical POS records will be preserved` },
        ],
      };
    } else {
      this.modalData = {
        icon: 'trash',
        title: `Delete **"${row['name']}"**?`,
        titleHighlight: 'danger',
        description: `**This action is permanent and cannot be undone.** Deleting this channel removes it from the configuration registry. Historical POS records are preserved as immutable audit data.`,
        effects: [
          { type: 'negative', text: `Channel definition **permanently removed** from the registry` },
          { type: 'negative', text: `Code **${row['code']}** released — can be reused by a new channel` },
          { type: 'positive', text: `${row['historicalPos']} historical POS records preserved as audit data` },
          { type: 'positive', text: `Audit trail preserved — full deletion event logged` },
        ],
        confirmation: {
          label: row['code'] as string,
          expectedValue: row['code'] as string,
          placeholder: `Type ${row['code']} to confirm`,
        },
        auditLog: true,
        actions: [{ label: 'Delete channel', style: 'danger', action: 'delete', disabledUntilConfirmed: true }],
      };
    }

    this.isModalOpen = true;
  }

  onModalAction(action: string): void {
    const row = this._pendingActionRow;
    if (!row) { this.closeModal(); return; }

    if (action === 'activate') {
      this.channelsService.activate(row['id']).subscribe({
        next: () => { this.closeModal(); this.loadChannels(); },
        error: (err) => { console.error('Failed to activate channel:', err); this.closeModal(); },
      });
    } else if (action === 'deactivate') {
      this.channelsService.deactivate(row['id']).subscribe({
        next: () => { this.closeModal(); this.loadChannels(); },
        error: (err) => { console.error('Failed to deactivate channel:', err); this.closeModal(); },
      });
    } else if (action === 'delete') {
      this.channelsService.delete(row['id']).subscribe({
        next: () => { this.closeModal(); this.loadChannels(); },
        error: (err) => { console.error('Failed to delete channel:', err); this.closeModal(); },
      });
    } else {
      this.closeModal();
    }
  }

  onModalDismiss(): void {
    this.closeModal();
  }

  private closeModal(): void {
    this.isModalOpen = false;
    this.modalData = null;
    this._pendingActionRow = null;
  }
}